import { useEffect, useState, useRef } from 'react';
import { liveChatAPI, cannedResponsesAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  MessageSquare, Users, Send, Phone, Mail, Globe, Monitor, Smartphone,
  Tablet, Clock, MapPin, ExternalLink, CheckCircle, XCircle, AlertCircle,
  RefreshCw, Loader2, Bot, User, Star, BarChart3, Download, Save, FileText,
  MoreVertical, Plus, Trash2, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AgentChatDashboardPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [availability, setAvailability] = useState({ status: 'offline', can_take_chats: true });
  const [cannedResponses, setCannedResponses] = useState([]);
  const [manageCannedOpen, setManageCannedOpen] = useState(false);
  const [cannedForm, setCannedForm] = useState({ title: '', content: '' });

  const fetchCanned = async () => {
    try {
      const res = await cannedResponsesAPI.getAll();
      setCannedResponses(res.data || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { fetchCanned(); }, []);

  const insertCanned = (content) => {
    setNewMessage(prev => (prev ? prev + ' ' : '') + content);
  };

  const addCanned = async () => {
    if (!cannedForm.title.trim() || !cannedForm.content.trim()) { toast.error('Title and content required'); return; }
    try {
      await cannedResponsesAPI.create(cannedForm);
      setCannedForm({ title: '', content: '' });
      toast.success('Canned response added');
      fetchCanned();
    } catch (e) { toast.error('Failed to add'); }
  };

  const deleteCanned = async (id) => {
    try {
      await cannedResponsesAPI.remove(id);
      fetchCanned();
    } catch (e) { toast.error('Failed to delete'); }
  };
  const [filter, setFilter] = useState('all');
  const [analytics, setAnalytics] = useState(null);
  const [ws, setWs] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchSessions();
    fetchAvailability();
    fetchAnalytics();

    // Polling fallback: production ingress may block WebSockets, so keep the
    // dashboard live by refreshing the session list on an interval regardless.
    const sessionsPoll = setInterval(fetchSessions, 8000);

    // Best-effort WebSocket for instant updates (ignored if it can't connect)
    let websocket = null;
    try {
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      websocket = new WebSocket(`${wsUrl}/ws/agent/${user?.id}`);
      websocket.onopen = () => console.log('Agent WebSocket connected');
      websocket.onmessage = (event) => {
        try { handleWebSocketMessage(JSON.parse(event.data)); } catch (e) { /* ignore */ }
      };
      websocket.onerror = () => { /* polling fallback covers this */ };
      setWs(websocket);
    } catch (e) {
      /* WebSocket unavailable — polling keeps things working */
    }

    return () => {
      clearInterval(sessionsPoll);
      if (websocket) {
        try { websocket.close(); } catch (e) { /* ignore */ }
      }
    };
  }, [user?.id]);

  // Poll messages for the open conversation (WS may be blocked in production)
  useEffect(() => {
    if (!selectedSession?.id) return;
    const int = setInterval(() => fetchMessages(selectedSession.id), 3000);
    return () => clearInterval(int);
  }, [selectedSession?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'new_chat':
        toast.info(`New chat from ${data.session?.visitor_name || 'Visitor'}`);
        fetchSessions();
        break;
      case 'escalation':
        toast.warning(`Chat escalation requested by ${data.visitor_name || 'Visitor'}`);
        fetchSessions();
        break;
      case 'message':
        if (selectedSession?.id === data.message?.session_id) {
          setMessages(prev => [...prev, data.message]);
        }
        break;
      case 'agent_status_change':
        // Another agent changed status
        break;
      default:
        break;
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await liveChatAPI.getSessions();
      setSessions(res.data || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      const res = await liveChatAPI.getAvailability();
      setAvailability(res.data);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await liveChatAPI.getAnalytics('week');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchMessages = async (sessionId) => {
    try {
      const res = await liveChatAPI.getMessages(sessionId);
      setMessages(res.data || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSelectSession = async (session) => {
    setSelectedSession(session);
    await fetchMessages(session.id);
  };

  const handleSaveTranscript = async () => {
    if (!selectedSession) return;
    try {
      await liveChatAPI.saveTranscript(selectedSession.id);
      toast.success('Transcript saved to database');
      setSelectedSession(prev => ({ ...prev, transcript_saved: true }));
    } catch (error) {
      toast.error('Failed to save transcript');
    }
  };

  const handleExportTranscript = async (format) => {
    if (!selectedSession) return;
    try {
      const res = await liveChatAPI.getTranscript(selectedSession.id, format);
      
      if (format === 'json') {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        downloadBlob(blob, `chat-transcript-${selectedSession.id}.json`);
      } else if (format === 'text') {
        const blob = new Blob([res.data.transcript], { type: 'text/plain' });
        downloadBlob(blob, `chat-transcript-${selectedSession.id}.txt`);
      } else if (format === 'html') {
        const blob = new Blob([res.data.transcript], { type: 'text/html' });
        downloadBlob(blob, `chat-transcript-${selectedSession.id}.html`);
      }
      
      toast.success(`Transcript exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export transcript');
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleStatusChange = async (status) => {
    try {
      await liveChatAPI.updateAvailability({ status });
      setAvailability(prev => ({ ...prev, status }));
      toast.success(`Status changed to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssignChat = async () => {
    if (!selectedSession) return;
    try {
      await liveChatAPI.assignChat(selectedSession.id);
      toast.success('Chat assigned to you');
      fetchSessions();
      setSelectedSession(prev => ({ ...prev, status: 'active', assigned_agent_id: user?.id }));
    } catch (error) {
      toast.error('Failed to assign chat');
    }
  };

  const handleCloseChat = async () => {
    if (!selectedSession) return;
    try {
      await liveChatAPI.closeChat(selectedSession.id);
      toast.success('Chat closed');
      fetchSessions();
      setSelectedSession(null);
      setMessages([]);
    } catch (error) {
      toast.error('Failed to close chat');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedSession) return;

    try {
      await liveChatAPI.sendMessage(selectedSession.id, {
        session_id: selectedSession.id,
        content: newMessage,
        sender_type: 'agent',
        sender_id: user?.id,
        sender_name: user?.full_name || user?.email
      });
      setNewMessage('');
      await fetchMessages(selectedSession.id);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      waiting: 'bg-amber-100 text-amber-700',
      active: 'bg-green-100 text-green-700',
      bot: 'bg-blue-100 text-blue-700',
      closed: 'bg-slate-100 text-slate-700',
      missed: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || styles.closed}>{status}</Badge>;
  };

  const getDeviceIcon = (deviceType) => {
    if (deviceType === 'mobile') return <Smartphone className="h-4 w-4" />;
    if (deviceType === 'tablet') return <Tablet className="h-4 w-4" />;
    return <Monitor className="h-4 w-4" />;
  };

  const filteredSessions = sessions.filter(s => {
    if (filter === 'all') return true;
    return s.status === filter;
  });

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="agent-chat-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="h-6 w-6" />
            Live Chat Dashboard
          </h1>
          <p className="text-muted-foreground">Manage customer conversations</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={availability.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[150px]" data-testid="agent-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="available">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Available
                </span>
              </SelectItem>
              <SelectItem value="busy">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Busy
                </span>
              </SelectItem>
              <SelectItem value="away">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-400 rounded-full"></span>
                  Away
                </span>
              </SelectItem>
              <SelectItem value="offline">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Offline
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchSessions}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      {analytics && (
        <div className="grid grid-cols-4 gap-4 mb-4">
          <Card className="p-3">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <MessageSquare className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.total_chats}</p>
                <p className="text-xs text-muted-foreground">Total Chats</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 p-2 rounded-lg">
                <Clock className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.waiting_chats}</p>
                <p className="text-xs text-muted-foreground">Waiting</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.active_chats}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Star className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.avg_satisfaction_rating?.toFixed(1) || '-'}</p>
                <p className="text-xs text-muted-foreground">Avg Rating</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
        {/* Sessions List */}
        <div className="col-span-4 flex flex-col">
          <Card className="flex-1 flex flex-col">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Conversations</CardTitle>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-[120px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="waiting">Waiting</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="bot">Bot</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="flex-1 p-0">
              <ScrollArea className="h-full">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : filteredSessions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No conversations</p>
                  </div>
                ) : (
                  <div className="space-y-1 p-2">
                    {filteredSessions.map((session) => (
                      <div
                        key={session.id}
                        onClick={() => handleSelectSession(session)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedSession?.id === session.id
                            ? 'bg-primary/10 border border-primary/20'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <p className="font-medium text-sm truncate">
                            {session.visitor_name || 'Visitor'}
                          </p>
                          {getStatusBadge(session.status)}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {session.visitor_info?.country && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {session.visitor_info.city}, {session.visitor_info.country_code}
                            </span>
                          )}
                          <span>•</span>
                          <span>{formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Chat Area */}
        <div className="col-span-5 flex flex-col">
          <Card className="flex-1 flex flex-col">
            {selectedSession ? (
              <>
                <CardHeader className="pb-2 border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">{selectedSession.visitor_name || 'Visitor'}</CardTitle>
                      <CardDescription className="text-xs">
                        {selectedSession.visitor_email || 'No email provided'}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedSession.status === 'waiting' || selectedSession.status === 'bot' ? (
                        <Button size="sm" onClick={handleAssignChat} data-testid="take-over-btn">
                          Take Over
                        </Button>
                      ) : selectedSession.status === 'active' ? (
                        <Button size="sm" variant="outline" onClick={handleCloseChat}>
                          Close Chat
                        </Button>
                      ) : null}
                      
                      {/* Save & Export Menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={handleSaveTranscript} disabled={selectedSession.transcript_saved}>
                            <Save className="h-4 w-4 mr-2" />
                            {selectedSession.transcript_saved ? 'Transcript Saved' : 'Save Transcript'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExportTranscript('text')}>
                            <FileText className="h-4 w-4 mr-2" />
                            Export as Text
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExportTranscript('html')}>
                            <Download className="h-4 w-4 mr-2" />
                            Export as HTML
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExportTranscript('json')}>
                            <Download className="h-4 w-4 mr-2" />
                            Export as JSON
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 p-0 flex flex-col min-h-0">
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-3">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_type === 'agent' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] p-3 rounded-lg ${
                              msg.sender_type === 'agent'
                                ? 'bg-primary text-primary-foreground'
                                : msg.sender_type === 'bot'
                                ? 'bg-blue-100 text-blue-900'
                                : msg.sender_type === 'system'
                                ? 'bg-slate-100 text-slate-600 text-center text-sm italic'
                                : 'bg-slate-100'
                            }`}
                          >
                            {msg.sender_type !== 'system' && (
                              <p className="text-xs font-medium mb-1 opacity-75">
                                {msg.sender_type === 'bot' && <Bot className="h-3 w-3 inline mr-1" />}
                                {msg.sender_name || msg.sender_type}
                              </p>
                            )}
                            <p className="text-sm">{msg.content}</p>
                            <p className="text-xs opacity-50 mt-1">
                              {format(new Date(msg.created_at), 'h:mm a')}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                  {(selectedSession.status === 'active' && selectedSession.assigned_agent_id === user?.id) && (
                    <form onSubmit={handleSendMessage} className="p-4 border-t">
                      {/* Canned responses quick-insert */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Zap className="h-3.5 w-3.5 text-amber-500" />
                        {cannedResponses.slice(0, 5).map(cr => (
                          <button
                            key={cr.id}
                            type="button"
                            onClick={() => insertCanned(cr.content)}
                            className="text-xs px-2 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                            title={cr.content}
                            data-testid={`canned-${cr.id}`}
                          >
                            {cr.title}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setManageCannedOpen(true)}
                          className="text-xs px-2 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50"
                          data-testid="manage-canned-btn"
                        >
                          Manage
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          data-testid="chat-message-input"
                        />
                        <Button type="submit" disabled={!newMessage.trim()}>
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select a conversation to start</p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Visitor Info Panel */}
        <div className="col-span-3 flex flex-col">
          <Card className="flex-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Visitor Info</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedSession?.visitor_info ? (
                <div className="space-y-4">
                  {/* Location */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase">Location</p>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {selectedSession.visitor_info.city}, {selectedSession.visitor_info.region}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground pl-6">
                      {selectedSession.visitor_info.country}
                    </p>
                  </div>

                  {/* Device */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase">Device</p>
                    <div className="flex items-center gap-2">
                      {getDeviceIcon(selectedSession.visitor_info.device_type)}
                      <span className="text-sm capitalize">{selectedSession.visitor_info.device_type}</span>
                    </div>
                    <p className="text-sm text-muted-foreground pl-6">
                      {selectedSession.visitor_info.browser} on {selectedSession.visitor_info.os}
                    </p>
                  </div>

                  {/* Source */}
                  {selectedSession.visitor_info.referrer_domain && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase">Source</p>
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{selectedSession.visitor_info.referrer_domain}</span>
                      </div>
                    </div>
                  )}

                  {/* Pages Visited */}
                  {selectedSession.visitor_info.page_count > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase">Activity</p>
                      <p className="text-sm">{selectedSession.visitor_info.page_count} pages viewed</p>
                    </div>
                  )}

                  {/* Contact Info */}
                  {(selectedSession.visitor_email || selectedSession.visitor_phone) && (
                    <div className="space-y-2 pt-4 border-t">
                      <p className="text-xs font-medium text-muted-foreground uppercase">Contact</p>
                      {selectedSession.visitor_email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{selectedSession.visitor_email}</span>
                        </div>
                      )}
                      {selectedSession.visitor_phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{selectedSession.visitor_phone}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* IP Info */}
                  <div className="space-y-2 pt-4 border-t">
                    <p className="text-xs font-medium text-muted-foreground uppercase">Network</p>
                    <p className="text-sm text-muted-foreground">
                      IP: {selectedSession.visitor_info.ip_address}
                    </p>
                    {selectedSession.visitor_info.isp && (
                      <p className="text-sm text-muted-foreground">
                        ISP: {selectedSession.visitor_info.isp}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No visitor selected</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Manage Canned Responses Dialog */}
      <Dialog open={manageCannedOpen} onOpenChange={setManageCannedOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Canned Responses</DialogTitle>
            <DialogDescription>Quick replies your team can insert into chats.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {cannedResponses.map(cr => (
              <div key={cr.id} className="flex items-start justify-between gap-2 p-2 border rounded-lg" data-testid={`canned-row-${cr.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm">{cr.title}</p>
                  <p className="text-xs text-muted-foreground">{cr.content}</p>
                </div>
                <Button variant="ghost" size="icon" className="text-red-500 flex-shrink-0" onClick={() => deleteCanned(cr.id)} data-testid={`delete-canned-${cr.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="border-t pt-3 space-y-2">
            <p className="text-sm font-medium">Add new</p>
            <Input placeholder="Title" value={cannedForm.title} onChange={(e) => setCannedForm(p => ({ ...p, title: e.target.value }))} data-testid="canned-title-input" />
            <Textarea placeholder="Message content" rows={2} value={cannedForm.content} onChange={(e) => setCannedForm(p => ({ ...p, content: e.target.value }))} data-testid="canned-content-input" />
            <Button onClick={addCanned} size="sm" data-testid="add-canned-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManageCannedOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentChatDashboardPage;
